/* eslint-disable no-nested-ternary */
import { ReceiptLong, Search } from '@mui/icons-material';
import { Avatar, Box, IconButton, Stack, Typography } from '@mui/material';
import { DatePicker } from '@mui/x-date-pickers';
import { format } from 'date-fns';
import { QueryFieldFilterConstraint, where } from 'firebase/firestore';
import { useMemo, useState } from 'react';
import { Order } from '../../apiTypes';
import { Column } from '../../components/DataTable';
import DataTablePaginated from '../../components/DataTablePaginated';
import OrderHistoryButton from '../../components/History/OrderHistoryButton';
import StyledTextField from '../../components/styled/StyledTextField';
import { t } from '../../constants/translations';
import { useAdmin } from '../../contexts/admin.context';
import { useCafe } from '../../contexts/cafe.context';
import { useLocale } from '../../contexts/locale.context';
import { usePagination } from '../../utils/api';
import currency from '../../utils/currency';
import { dateLocaleMap } from '../../utils/dates';
import getDayDateRange from '../../utils/dates/getDayDateRange';
import useDebounce from '../../utils/useDebounce';

import BillPrintModal from '../POS/BillPrintModal';
import CostPersent from './CostPersent';

type Row = DbDocument<Order>;

const PaidOrders = () => {
  const [date, setDate] = useState<Date | null>(new Date());
  const { lang } = useLocale();
  const { cafeSlug, cafe } = useAdmin();
  const { workersMap, worker } = useCafe();

  const [bill, setBill] = useState<DbDocument<Order> | null>(null);
  const [search, setSearch] = useState<'' | number>('');

  const debouncedSearch = useDebounce(Number(search), 400);

  const dateRange = useMemo(() => getDayDateRange(date), [date]);

  /** @todo remove */
  const startDate = cafeSlug === 'lm64cv5j' && dateRange.start < 1711494000000 ? 1711494000000 : dateRange.start;

  const whereQuery: QueryFieldFilterConstraint[] = useMemo(
    () =>
      debouncedSearch
        ? [where('number', '==', debouncedSearch)]
        : [where('paidTime', '<', dateRange.end), where('paidTime', '>', startDate)],
    [debouncedSearch, dateRange.end, startDate]
  );

  const { page, onPageChange, data, isLoading, count } = usePagination<Order>({
    path: `cafes/${cafeSlug}/orders`,
    orderByField: 'paidTime',
    where: whereQuery,
  });

  const orders = useMemo(
    () => (worker?.credentials.view_archive_of_other_employees ? data : data?.filter((o) => o.workerId === worker?.id)),
    [data, worker?.credentials.view_archive_of_other_employees, worker?.id]
  );

  const columns: Column<Row>[] = useMemo(
    () => [
      {
        name: 'number',
        title: t.number[lang],
        align: 'left',
        render: (r: Row) => <Typography>{r.number}</Typography>,
      },
      {
        name: 'time',
        title: t.creation_time[lang],
        align: 'center',
        render: (r: Row) => (
          <Typography>{format(new Date(r.created), 'dd MMM HH:mm', { locale: dateLocaleMap[lang] })}</Typography>
        ),
      },
      {
        name: 'tableName',
        title: t.table[lang],
        align: 'center',
        render: (r: Row) => <Typography>{r.tableName}</Typography>,
        filterable: true,
      },
      {
        name: 'workerName',
        title: t.author[lang],
        align: 'center',
        render: (r: Row) => (
          <Stack direction="row" alignItems="center" spacing={{ xs: 1 }}>
            <Avatar src={workersMap[r.workerId]?.photoUrl} />
            <span>{workersMap[r.workerId]?.name || r.workerName}</span>
          </Stack>
        ),
        filterable: true,
      },
      {
        name: 'amount',
        title: t.amount[lang],
        align: 'center',
        render: (r: Row) => (
          <Typography color={(t) => (r.paid ? t.palette.text.primary : t.palette.warning.main)}>
            {r.paid ? currency(r.paid?.total, cafe?.currency, true) : r.closed ? t.removed[lang] : t.not_closed[lang]}
          </Typography>
        ),
      },
      {
        name: 'cost',
        title: t.estimated_cost_price[lang],
        align: 'center',
        render: (r: Row) => <CostPersent order={r} />,
      },
      {
        name: 'bill',
        title: '',
        align: 'right',
        render: (r: Row) => (
          <IconButton onClick={() => setBill(r)} disabled={!worker?.credentials.print_bill}>
            <ReceiptLong />
          </IconButton>
        ),
      },
      {
        name: 'history',
        title: '',
        align: 'right',
        render: (r: Row) => <OrderHistoryButton order={r} />,
      },
    ],
    [cafe?.currency, lang, worker?.credentials.print_bill, workersMap]
  );

  return (
    <Box sx={{ maxWidth: 800, m: '0 auto', pb: 2 }}>
      <DataTablePaginated<Row>
        columns={columns}
        rows={orders}
        perPage={10}
        page={debouncedSearch ? 0 : page}
        count={debouncedSearch ? 1 : count}
        onPageChange={onPageChange}
        loading={isLoading}
        customHeader={
          <Box sx={{ display: 'flex', gap: 2 }}>
            <DatePicker
              label={t.date[lang]}
              value={date}
              onChange={(newValue) => setDate(newValue)}
              renderInput={(params) => <StyledTextField {...params} size="small" />}
              disableFuture
              disabled={!!debouncedSearch}
            />

            <StyledTextField
              label={t.number[lang]}
              value={search}
              onChange={(e) => {
                if (!e.target.value) {
                  setSearch('');
                }
                if (Number(e.target.value)) {
                  setSearch(Number(e.target.value));
                }
              }}
              size="small"
              InputProps={{ endAdornment: <Search /> }}
            />
          </Box>
        }
      />

      {bill && (
        <BillPrintModal
          service={bill.paid?.service || 0}
          discount={bill.paid?.discount || 0}
          onClose={() => setBill(null)}
          order={bill}
          promotionsApplied={bill.paid?.promotionsApplied || []}
        />
      )}
    </Box>
  );
};

export default PaidOrders;
